import React, { useState, useEffect } from 'react';
import { Calculator, Users, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CustomerRetentionRateCalculator = () => {
  const [customersStart, setCustomersStart] = useState(1000);
  const [customersEnd, setCustomersEnd] = useState(800);
  const [newCustomers, setNewCustomers] = useState(200);
  const [retentionRate, setRetentionRate] = useState(0);

  useEffect(() => {
    calculateRetentionRate();
  }, [customersStart, customersEnd, newCustomers]);

  const calculateRetentionRate = () => {
    const rate = ((customersEnd - newCustomers) / customersStart) * 100;
    setRetentionRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-retention-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Customer Retention Rate Calculator: Measure & Boost Customer Loyalty | Free Tool</title>
        <meta name="description" content="Calculate your Customer Retention Rate instantly. Track customer loyalty, compare against industry benchmarks, and get expert tips to improve retention." />
        <meta name="keywords" content="customer retention rate calculator, retention metrics, customer loyalty calculator, churn rate calculator, customer retention tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Customer Retention Rate Calculator: Boost Customer Loyalty" />
        <meta property="og:description" content="Calculate your Customer Retention Rate instantly. Get actionable insights to improve customer loyalty and reduce churn." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Customer Retention Rate Calculator: Boost Customer Loyalty" />
        <meta property="twitter:description" content="Calculate your Customer Retention Rate instantly. Get actionable insights to improve customer loyalty and reduce churn." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Customer Retention Rate Calculator",
            "description": "Free calculator to measure and improve customer retention rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Customer Retention Rate",
              "Track customer loyalty",
              "Compare with industry benchmarks",
              "Get retention improvement tips",
              "Monitor customer churn"
            ],
            "about": {
              "@type": "Thing",
              "name": "Customer Retention Rate",
              "description": "The percentage of customers a business retains over a given period, excluding new customers acquired during that period."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Business Owners, Customer Success Managers, Marketing Professionals"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Customer Retention Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of customers at start of period"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of customers at end of period"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add number of new customers acquired"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your retention rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Customer Retention Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Customers at Start of Period</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={customersStart}
                  onChange={(e) => setCustomersStart(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Customers at End of Period</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={customersEnd}
                  onChange={(e) => setCustomersEnd(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="800"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Customers Acquired</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={newCustomers}
                  onChange={(e) => setNewCustomers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {retentionRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Customer Retention Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Customer Retention Rate is {retentionRate}%. This means that you retained {retentionRate}% of your existing customers during the given period. A higher retention rate indicates better customer loyalty and satisfaction.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>SaaS: 85-95%</li>
          <li>E-commerce: 20-35%</li>
          <li>Retail: 60-70%</li>
          <li>Banking: 75-85%</li>
          <li>Insurance: 80-90%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Improve Customer Retention</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Provide excellent customer service and support</li>
          <li>Implement a customer loyalty program</li>
          <li>Regularly engage with customers through personalized communication</li>
          <li>Continuously improve your product or service based on customer feedback</li>
          <li>Offer exclusive deals or early access to loyal customers</li>
          <li>Create a strong onboarding process</li>
          <li>Develop a customer feedback loop</li>
          <li>Monitor and reduce customer pain points</li>
          <li>Implement proactive customer success strategies</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact of Customer Retention</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Increasing retention by 5% can increase profits by 25-95%</li>
          <li>The cost of acquiring a new customer is 5-25x higher than retaining one</li>
          <li>Loyal customers are 50% more likely to try new products</li>
          <li>Existing customers spend 31% more compared to new customers</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerRetentionRateCalculator;
